/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";

import { safeOrigin } from "./environment";
import type { TraceRepository } from "./TraceRepository";
import { TRACE_VERSION, type TraceUploadAcknowledgement } from "./types";

const RETRY_DELAYS_MS = [
  2_000, 5_000, 15_000, 30_000, 60_000, 300_000,
] as const;
const FETCH_MAX_EVENTS = 100;
const FETCH_MAX_BYTES = 128 * 1024;
const BEACON_MAX_EVENTS = 50;
const BEACON_MAX_BYTES = 60 * 1024;
const BEACON_RETRY_DELAY_MS = 5_000;

/** Проверяет, что сервер подтвердил именно отправленный пакет целиком. */
export function isValidTraceAcknowledgement(
  value: unknown,
  uploadId: string,
  eventCount: number,
): value is TraceUploadAcknowledgement {
  if (!value || typeof value !== "object") return false;
  const acknowledgement = value as Partial<TraceUploadAcknowledgement>;
  return (
    acknowledgement.traceVersion === TRACE_VERSION &&
    acknowledgement.uploadId === uploadId &&
    acknowledgement.acceptedEventCount === eventCount
  );
}

/** Асинхронно выгружает outbox, не блокируя пользовательские операции. */
export class TraceUploader {
  private readonly owner = crypto.randomUUID();
  private endpoint: string | null = null;
  private timer: number | null = null;
  private running = false;
  private retryIndex = 0;
  private nextAllowedAt = 0;

  public constructor(private readonly repository: TraceRepository) {}

  public configure(enabled: boolean, endpoint: string | undefined): void {
    this.endpoint =
      enabled && endpoint?.startsWith("https://") ? endpoint : null;
    if (this.endpoint) this.start();
    else this.stop();
  }

  public start(): void {
    if (!this.endpoint || this.timer !== null) return;
    this.timer = window.setInterval(() => void this.flush(), 15_000);
    window.addEventListener("online", this.onOnline);
    window.addEventListener("pageshow", this.onPageShow);
    void this.flush();
  }

  public stop(): void {
    if (this.timer !== null) window.clearInterval(this.timer);
    this.timer = null;
    window.removeEventListener("online", this.onOnline);
    window.removeEventListener("pageshow", this.onPageShow);
  }

  private readonly onOnline = (): void => {
    void this.flush();
  };

  private readonly onPageShow = (): void => {
    void this.flush();
  };

  public async flush(useBeacon = false): Promise<void> {
    if (
      this.running ||
      !this.endpoint ||
      !navigator.onLine ||
      Date.now() < this.nextAllowedAt
    )
      return;
    this.running = true;
    try {
      for (;;) {
        const batch = await this.repository.leasePendingBatch(
          this.owner,
          useBeacon ? BEACON_MAX_EVENTS : FETCH_MAX_EVENTS,
          useBeacon ? BEACON_MAX_BYTES : FETCH_MAX_BYTES,
        );
        if (!batch) break;
        const body = JSON.stringify(batch.envelope);
        if (
          useBeacon &&
          navigator.sendBeacon?.(
            this.endpoint,
            new Blob([body], { type: "application/json" }),
          )
        ) {
          // Beacon не даёт прочитать acknowledgement: откладываем подтверждаемую
          // fetch-доставку, чтобы не дублировать запрос сразу после pagehide.
          const nextAttemptAt = new Date(
            Date.now() + BEACON_RETRY_DELAY_MS,
          ).toISOString();
          await this.repository.failUpload(
            batch.upload,
            false,
            nextAttemptAt,
            null,
            "beacon_unconfirmed",
          );
          window.setTimeout(() => {
            if (document.visibilityState === "visible") void this.flush();
          }, BEACON_RETRY_DELAY_MS);
          break;
        }

        try {
          const response = await fetch(this.endpoint, {
            method: "POST",
            credentials: "omit",
            headers: { "Content-Type": "application/json" },
            body,
            keepalive: useBeacon,
          });
          if (response.ok) {
            const acknowledgement: unknown = await response
              .json()
              .catch(() => null);
            if (
              isValidTraceAcknowledgement(
                acknowledgement,
                batch.upload.uploadId,
                batch.upload.eventCount,
              )
            ) {
              await this.repository.acknowledge(batch.upload);
              this.retryIndex = 0;
              this.nextAllowedAt = 0;
              continue;
            }
            await this.handleFailure(
              batch.upload,
              false,
              response.status,
              "invalid_acknowledgement",
            );
            break;
          }
          const retryable =
            response.status === 408 ||
            response.status === 429 ||
            response.status >= 500;
          await this.handleFailure(
            batch.upload,
            !retryable,
            response.status,
            `http_${response.status}`,
          );
          break;
        } catch (error) {
          logger.warn("[CallTrace] Не удалось отправить пакет телеметрии", {
            endpointOrigin: safeOrigin(this.endpoint),
            errorName: error instanceof Error ? error.name : "unknown",
          });
          await this.handleFailure(batch.upload, false, null, "network_error");
          break;
        }
      }
    } finally {
      this.running = false;
    }
  }

  private async handleFailure(
    upload: Parameters<TraceRepository["failUpload"]>[0],
    dead: boolean,
    status: number | null,
    code: string,
  ): Promise<void> {
    const delay =
      RETRY_DELAYS_MS[Math.min(this.retryIndex, RETRY_DELAYS_MS.length - 1)];
    this.retryIndex += 1;
    const jittered = Math.round(delay * (0.8 + Math.random() * 0.4));
    const nextAttemptAt = dead
      ? null
      : new Date(Date.now() + jittered).toISOString();
    this.nextAllowedAt = dead ? 0 : Date.parse(nextAttemptAt!);
    await this.repository.failUpload(upload, dead, nextAttemptAt, status, code);
    if (!dead) window.setTimeout(() => void this.flush(), jittered);
  }
}
